import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGolfFlagHole, faPersonWalking, faUsers } from "@fortawesome/pro-solid-svg-icons";
import React, { Fragment } from "react";
import "./CustomerTeeTimeCard.scss";
import { useTranslation } from "react-i18next";
import { displayCurrency } from "helpers/Helpers";
import classNames from "classnames";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";
import { CustomIcon } from "components/customIcon/CustomIcon";
import Spin from "components/spin/spin";
import { faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { Badge } from "components/badge/Badge";

interface IProps {
  time: string;
  isPrepaid: boolean;
  isShotgun: boolean;
  spotsRemaining: number;
  holes: number[];
  cartRule: string;
  nineHolePrice: number;
  eighteenHolePrice: number;
  dynamicPercentVariation: number;
  onClick: () => void;
  courseName?: string;
  divisionTitle?: string;
  singleBookings?: string;
  startingHole?: string;
  isLeague?: boolean;
  facilityName?: string;
  restricted?: boolean;
}

export function CustomerTeeTimeCard(props: IProps) {
  const {
    time,
    isPrepaid,
    isShotgun,
    spotsRemaining,
    holes,
    cartRule,
    nineHolePrice,
    eighteenHolePrice,
    dynamicPercentVariation,
    onClick,
    courseName,
    divisionTitle,
    singleBookings,
    startingHole,
    isLeague,
    facilityName,
  } = props;

  const windowSize = useWindowSize();

  const { t } = useTranslation();

  function convertCartRuleToDescription(cartRule: "none" | "optional" | "required") {
    if (cartRule === "none") {
      return t("Walking");
    } else if (cartRule === "optional") {
      return t("Carts Available");
    } else if (cartRule === "required") {
      return t("Carts Required");
    }
  }

  const teeTimeBadges = (mobile = false) => {
    return (
      <>
        {isLeague && (
          <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
            LEAGUE
          </div>
        )}
        {isPrepaid ? (
          <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
            {t("PRE-PAID")}
          </div>
        ) : null}
        {isShotgun ? (
          <>
            <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
              {"SHOTGUN"}
            </div>
            <div className={classNames("customer-tee-time-prepaid", { "customer-tee-time-prepaid-mobile": mobile })}>
              {startingHole}
            </div>
          </>
        ) : null}
      </>
    );
  };

  const dynamicPrices = {
    nineHole: nineHolePrice + (nineHolePrice * (dynamicPercentVariation ?? 0)) / 100,
    eighteenHole: eighteenHolePrice + (eighteenHolePrice * (dynamicPercentVariation ?? 0)) / 100,
  };

  return (
    <div className="customer-tee-time" onClick={onClick}>
      <div className="customer-tee-time-content">
        <div className="customer-tee-time-details-container">
          <div className="customer-tee-time-primary-details">
            <div className="customer-tee-time-active-time">{time}</div>
            {teeTimeBadges()}
            {facilityName && windowSize?.width > MOBILE_WIDTH && (
              <p className="customer-tee-time-facility-name ">{facilityName}</p>
            )}
            {props?.restricted ? ( <OverlayTrigger
                  placement="auto"
                  overlay={<Tooltip id="Restricted-booking"><>{t("Restricted tee time")} <br/> {t("Your member rate is not valid at this time. Regular green fee applies.")}</></Tooltip>}
                ><span><Badge type="error" size="small">{t("Restricted booking")} <span className="ml-2"><FontAwesomeIcon icon={faCircleExclamation} /></span></Badge></span></OverlayTrigger>) : null}
          </div>
          {facilityName && windowSize?.width <= MOBILE_WIDTH && (
            <p className="customer-tee-time-facility-name ">{facilityName}</p>
          )}
          <div className="customer-tee-time-secondary-details">
            {courseName ? <p>{courseName}</p> : null}
            {divisionTitle ? <p>{divisionTitle}</p> : null}
          </div>
          <div
            className={classNames("customer-tee-time-primary-details customer-tee-time-badges-container", {
              "customer-tee-time-hide-badges-container": !(isLeague || isPrepaid || isShotgun),
            })}
          >
            {teeTimeBadges(true)}
          </div>
          <div className="customer-tee-time-tertiary-details">
            <div className="customer-tee-time-detail">
              {windowSize.width < TABLET_WIDTH ? (
                <Fragment>
                  <span>
                    {spotsRemaining === 1
                      ? `1 ${t("Player")}`
                      : `${
                          singleBookings === "none" || (singleBookings === "allow_within_group" && spotsRemaining === 4)
                            ? "2"
                            : "1"
                        }
                  ${singleBookings === "none" && spotsRemaining <= 2 ? "" : `- ${spotsRemaining}`} ${t(
                          "Players",
                        )}`}
                  </span>
                </Fragment>
              ) : (
                <Fragment>
                  <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faUsers} />
                  <span>
                    {spotsRemaining === 1
                      ? `1 ${t("Player")}`
                      : `${
                          singleBookings === "none" || (singleBookings === "allow_within_group" && spotsRemaining === 4)
                            ? "2"
                            : "1"
                        }
                  ${singleBookings === "none" && spotsRemaining <= 2 ? "" : `- ${spotsRemaining}`} ${t(
                          "Players",
                        )}`}
                  </span>
                </Fragment>
              )}
            </div>
            <div className="customer-tee-time-detail">
              {windowSize.width < TABLET_WIDTH ? (
                <Fragment>
                  <span>{`${holes.join(" / ")} ${t(
                    "Holes",
                  )}`}</span>
                </Fragment>
              ) : (
                <Fragment>
                  <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faGolfFlagHole} />
                  <span>{`${holes.join(" / ")} ${t(
                    "Holes",
                  )}`}</span>
                </Fragment>
              )}
            </div>

            {cartRule === "none" || cartRule === "optional" || cartRule === "required" ? (
              <div className="customer-tee-time-detail">
                {windowSize.width < TABLET_WIDTH ? (
                  <Fragment>
                    <span>{convertCartRuleToDescription(cartRule)}</span>
                  </Fragment>
                ) : (
                  <Fragment>
                    {cartRule === "none" && (
                      <FontAwesomeIcon className="customer-tee-time-detail-icon" icon={faPersonWalking} />
                    )}

                    <div className="mr-2">
                      {cartRule === "optional" || cartRule === "required" ? (
                        <CustomIcon colour="blue" src="../../../public/images/powercart.svg" size="medium" />
                      ) : null}
                    </div>

                    <span>{convertCartRuleToDescription(cartRule)}</span>
                  </Fragment>
                )}
              </div>
            ) : (
              <div />
            )}
          </div>
        </div>

        <div className="customer-tee-time-pricing">
          <div className="customer-tee-time-price-container">
            {eighteenHolePrice === undefined && nineHolePrice === undefined ? (
              <div style={{ display: "flex", height: "100%", margin: "auto" }}>
                <div className="text-center m-auto">
                  <div style={{ height: "32px" }}>
                    <Spin />
                  </div>
                </div>
              </div>
            ) : null}
            {nineHolePrice != null && (
              <div className="holes-pricing-container">
                <p className="customer-tee-time-holes-text">9 Holes</p>
                <p className="customer-tee-time-price">{displayCurrency("cad", dynamicPrices.nineHole)}</p>
              </div>
            )}
            {eighteenHolePrice != null && (
              <div className="holes-pricing-container">
                <p className="customer-tee-time-holes-text">18 Holes</p>
                <span className="customer-tee-time-price">{displayCurrency("cad", dynamicPrices.eighteenHole)}</span>
              </div>
            )}
          </div>
          <button className="customer-tee-time-select-price">
            {t("Select")}
          </button>
        </div>
      </div>
    </div>
  );
}
